import { useCallback, useLayoutEffect, useState } from 'react';
import { getConfig } from './get-config';
import { getScheduleSettings } from './get-schedule-settings';
import { timeSolvers } from './repeat-solvers';
import type { ScheduleDefaultsType } from './schedule-defaults';

interface UseTimeToTheEndInScheduleProps<T extends ScheduleDefaultsType> {
  settings: T;
  enabled?: boolean;
  _debugUserDate?: Date;
}

export function useTimeToEndSchedule<T extends ScheduleDefaultsType>({
  settings,
  enabled,
  _debugUserDate
}: UseTimeToTheEndInScheduleProps<T>) {
  const {
    scheduleStart,
    scheduleEnd,
    scheduleIsAllDay,
    scheduleTimeZone,
    scheduleRepeatPeriod,
    scheduleRepeatFrequency,
    scheduleRepeatInterval,
    scheduleRepeatWeeklyOnDays,
    scheduleRepeatMonthlyOnDay,
    scheduleRepeatEnds,
    scheduleRepeatEndsDate,
    scheduleRepeatEndsOccurrences
  } = getScheduleSettings(settings);

  const calculateTimeToTheEnd = useCallback(() => {
    if (!enabled) {
      return null;
    }

    const { repeatType, startDate, endDate, userDate, freq } = getConfig(
      {
        scheduleStart,
        scheduleEnd,
        scheduleIsAllDay,
        scheduleTimeZone,
        scheduleRepeatPeriod,
        scheduleRepeatFrequency,
        scheduleRepeatInterval,
        scheduleRepeatWeeklyOnDays,
        scheduleRepeatMonthlyOnDay,
        scheduleRepeatEnds,
        scheduleRepeatEndsDate,
        scheduleRepeatEndsOccurrences
      },
      _debugUserDate
    );

    const solver = timeSolvers[repeatType];

    return solver({
      startDate,
      endDate,
      userDate,
      freq
    });
  }, [
    enabled,
    _debugUserDate,
    scheduleStart,
    scheduleEnd,
    scheduleIsAllDay,
    scheduleTimeZone,
    scheduleRepeatPeriod,
    scheduleRepeatFrequency,
    scheduleRepeatInterval,
    scheduleRepeatWeeklyOnDays,
    scheduleRepeatMonthlyOnDay,
    scheduleRepeatEnds,
    scheduleRepeatEndsDate,
    scheduleRepeatEndsOccurrences
  ]);

  const [timeToTheEnd, setTimeToTheEnd] = useState(calculateTimeToTheEnd);
  const recalculateTimeToTheEnd = useCallback(() => {
    setTimeToTheEnd(calculateTimeToTheEnd());
  }, [calculateTimeToTheEnd]);
  useLayoutEffect(recalculateTimeToTheEnd, [recalculateTimeToTheEnd]);

  return {
    timeToTheEnd,
    recalculateTimeToTheEnd
  };
}
